// Aplikační shell (F1 1.3): hlavička + levá lišta + obsah. Menu se skládá výhradně z GET /ja
// (kategorie a sekce od serveru, popisky podle role), osobní nastavení (téma, lišta, oblíbené)
// drží PoskytovatelNastaveni. Obrazovky sekcí se vykreslují do <Outlet/>. Na telefonu (≤ 560 px, krok 13.7a)
// navíc spodní lišta (SpodniLista.tsx): Domů, tři sekce a Menu, které vysune lištu, a nad širokou tabulkou věta
// „Pohodlnější na tabletu nebo počítači.“ (siroke-tabulky.ts, krok 13.7c–e). Spodní lištu má i dotykový tablet na výšku
// (pod 950 px, rozhodnutí 05/2940 a, W51) — tam zůstává i tlačítko menu v hlavičce.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { lina, NacitaniCasti } from '../komponenty/NacitaniCasti';
import { useNovaVerze } from '../pwa/nova-verze';
import { usePrihlaseni } from '../prihlaseni/stav-prihlaseni';
import { Hlavicka, NahledPruh } from './Hlavicka';
import { Lista } from './Lista';
import { nazevSekcePodleRole, sestavitMenu, vsechnyPolozky } from './menu';
import { PoskytovatelNastaveni, useNastaveni } from './nastaveni-uzivatele';
import { klicSekceZCesty } from './sekce-cesty';
import { useOznacitSirokeTabulky } from './siroke-tabulky';
import { SpodniLista } from './SpodniLista';
import { AKCE_MENU_OFICIALNI } from '../api/menu-lista';
import { useJeTelefon, useMaSpodniListu } from './telefon';
import './Shell.css';

/** Sekce, kde jsou osobní údaje a mzdy (docs/01, 30. 10. 2026): snímek obrazovky k hlášení je rozmazaný. */
const SEKCE_S_OSOBNIMI_UDAJI: ReadonlySet<string> = new Set(['zamestnanci', 'dochazka']);

// Pruh „Nemáte zahájenou směnu“ (W66 díl 4) se stahuje, jen komu se docházka počítá — hlavní balík kvůli němu neroste.
const PruhSmeny = lina(() => import('../sekce/dochazka/PruhSmeny').then((m) => m.PruhSmeny));
// Okno „Co je nového“ a pruh nové verze se ukážou jen občas — jejich kód se stáhne až po startu (rozpočet hlavního
// balíku, `vite.config.ts`); do té doby nic nevykreslí (`tiche`), stejně jako když není co ukázat.
const CoJeNoveho = lina(() => import('../novinky/CoJeNoveho').then((m) => m.CoJeNoveho));
const PruhNoveVerze = lina(() => import('../pwa/PruhNoveVerze').then((m) => m.PruhNoveVerze));

function ShellVnitrek() {
  const { ja } = usePrihlaseni();
  const { nastaveni } = useNastaveni();
  const umisteni = useLocation();
  const [menuOtevrene, setMenuOtevrene] = useState(false);
  // Obsah se vykreslí, až pruh směny ví, jestli se ukáže (jinak by ho po odpovědi serveru posunul dolů).
  const [pruhPripraven, setPruhPripraven] = useState(false);
  const pruhHotov = useCallback(() => setPruhPripraven(true), []);
  // Pojistka, kdyby se kód pruhu nepodařilo stáhnout: obsah se neskrývá déle než pár sekund.
  useEffect(() => {
    const casovac = window.setTimeout(pruhHotov, 3000);
    return () => window.clearTimeout(casovac);
  }, [pruhHotov]);
  // Kód pruhu se stahuje hned, ne až po prvním vykreslení shellu — rozhodnutí „ukáže se / neukáže“ pak přijde dřív.
  const mozePruh = (ja?.akce ?? []).includes('dochazka.vlastni');
  useEffect(() => {
    if (mozePruh) void import('../sekce/dochazka/PruhSmeny');
  }, [mozePruh]);
  const verze = useNovaVerze();
  const jeTelefon = useJeTelefon();
  const maSpodniListu = useMaSpodniListu();
  useOznacitSirokeTabulky(jeTelefon);

  // Osobní pořadí položek (Nastavení › Menu) platí pro všechny kromě toho, kdo spravuje oficiální menu — ten pracuje
  // vždy s oficiálním pořadím (rozhodnutí v 01).
  const osobniPoradi = ja?.akce?.includes(AKCE_MENU_OFICIALNI)
    ? null
    : nastaveni.menu_osobni_poradi;
  const menu = useMemo(() => (ja ? sestavitMenu(ja, osobniPoradi) : null), [ja, osobniPoradi]);
  const klic = klicSekceZCesty(umisteni.pathname);
  const polozka = menu && klic ? vsechnyPolozky(menu).find((p) => p.klic === klic) : undefined;
  const nazevSekce = polozka?.nazev ?? (klic && ja ? nazevSekcePodleRole(klic, ja) : undefined);

  useEffect(() => {
    document.title = nazevSekce ? `${nazevSekce} · ECOser App` : 'ECOser App';
    return () => {
      document.title = 'ECOser App';
    };
  }, [nazevSekce]);

  // Vysunutá lišta se po přechodu zavře.
  useEffect(() => {
    setMenuOtevrene(false);
  }, [umisteni.pathname]);

  if (!ja || !menu) return null;

  const sirkaListy = nastaveni.rail_collapsed ? 58 : nastaveni.rail_width;
  // Pruh směny jen komu se docházka počítá (akce + příznak účtu); zda se právě ukáže, rozhoduje server.
  // Na Kalendáři se pruh neukazuje (rozhodnutí zadavatele 13. 10. 2026, 05/3319, doplněk): na výšku 600 px by tam nadpis dne
  // sjel pod spodní lištu a kalendář pruh nepotřebuje.
  const naKalendari =
    umisteni.pathname === '/kalendar' || umisteni.pathname.startsWith('/kalendar/');
  const maPruhSmeny =
    (ja.akce ?? []).includes('dochazka.vlastni') &&
    ja.priznaky?.['dochazka_vlastni'] !== false &&
    !naKalendari;
  // Pruhy pod hlavičkou (náhled za uživatele, nová verze) posouvají lištu — `--pruhy-vyska` v Shell.css.
  const tridy = [
    'shell',
    menuOtevrene ? 'shell--menu-otevrene' : '',
    ja.nahled_za ? 'shell--nahled' : '',
    // Kritické nasazení (05/414) pruh ukáže, i když ho uživatel předtím zavřel.
    verze.kDispozici && (!verze.zavreno || verze.kriticka) ? 'shell--nova-verze' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={tridy}
      // Jen uživatelská šířka — `--lista-sirka` skládá Shell.css (pod prahem 0 px, lišta je vysouvací).
      style={{ '--lista-sirka-uzivatel': `${sirkaListy}px` } as React.CSSProperties}
    >
      <a href="#obsah" className="preskocit">
        Přeskočit na obsah
      </a>
      <Hlavicka
        ja={ja}
        nazevSekce={klic === 'home' ? undefined : nazevSekce}
        menuOtevrene={menuOtevrene}
        naMenu={() => setMenuOtevrene((o) => !o)}
      />
      <NahledPruh ja={ja} />
      <NacitaniCasti tiche>
        <PruhNoveVerze />
      </NacitaniCasti>
      <Lista
        menu={menu}
        aktivniKlic={klic}
        otevrena={menuOtevrene}
        zavrit={() => setMenuOtevrene(false)}
      />
      <main
        id="obsah"
        className="shell__obsah"
        tabIndex={-1}
        // Osobní údaje a mzdy: na snímku obrazovky k hlášení se celá sekce rozmaže (shell/snimek-obrazovky.ts).
        data-citlive={
          klic && SEKCE_S_OSOBNIMI_UDAJI.has(klic.split(':')[0]!) ? 'rozmazat' : undefined
        }
      >
        {maPruhSmeny && (
          <NacitaniCasti>
            <PruhSmeny ja={ja} naPripraveno={pruhHotov} />
          </NacitaniCasti>
        )}
        {(!maPruhSmeny || pruhPripraven) && <Outlet />}
      </main>
      <NacitaniCasti tiche>
        <CoJeNoveho ja={ja} />
      </NacitaniCasti>
      {maSpodniListu && (
        <SpodniLista
          ja={ja}
          menu={menu}
          aktivniKlic={klic}
          menuOtevrene={menuOtevrene}
          naMenu={() => setMenuOtevrene((o) => !o)}
        />
      )}
    </div>
  );
}

export function Shell() {
  const { ja } = usePrihlaseni();
  const zamestnanecId = ja?.zamestnanec?.id ?? 'neznamy';
  return (
    <PoskytovatelNastaveni zamestnanecId={zamestnanecId}>
      <ShellVnitrek />
    </PoskytovatelNastaveni>
  );
}
